import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { mapRoleTaxonomySelectionToId } from '../../../../shared/user-preferences/utils/taxonomy-mapper';
import {
  DEFAULT_ACTIVITY_TAXONOMY_ID,
  DEFAULT_SKILL_TAXONOMY_ID,
} from '../contexts/defaultFilters';
import type { CustomActivityTaxonomySelection } from '../definitions/activityTaxonomy';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../types/filters';
import { stripVersionFromTaxonomyId } from './taxonomyHelpers';

const readRoleTaxonomyFilter = (
  effectiveFilters: Filter[],
  activeFilterMap?: ReadonlyMap<FilterId, Filter>
): SingleValueFilter<CustomRoleTaxonomySelection> | undefined => {
  const fromMap = activeFilterMap?.get(FilterId.ROLE_TAXONOMY);
  if (fromMap?.type === FilterType.VALUE) {
    return fromMap as SingleValueFilter<CustomRoleTaxonomySelection>;
  }
  return effectiveFilters.find(
    (f): f is SingleValueFilter<CustomRoleTaxonomySelection> =>
      f.id === FilterId.ROLE_TAXONOMY && f.type === FilterType.VALUE
  );
};

const readSkillTaxonomyFilter = (
  effectiveFilters: Filter[],
  activeFilterMap?: ReadonlyMap<FilterId, Filter>
): SingleValueFilter<CustomSkillTaxonomySelection> | undefined => {
  const fromMap = activeFilterMap?.get(FilterId.SKILL_TAXONOMY);
  if (fromMap?.type === FilterType.VALUE) {
    return fromMap as SingleValueFilter<CustomSkillTaxonomySelection>;
  }
  return effectiveFilters.find(
    (f): f is SingleValueFilter<CustomSkillTaxonomySelection> =>
      f.id === FilterId.SKILL_TAXONOMY && f.type === FilterType.VALUE
  );
};

const readActivityTaxonomyFilter = (
  effectiveFilters: Filter[],
  activeFilterMap?: ReadonlyMap<FilterId, Filter>
): SingleValueFilter<CustomActivityTaxonomySelection> | undefined => {
  const fromMap = activeFilterMap?.get(FilterId.ACTIVITY_TAXONOMY);
  if (fromMap?.type === FilterType.VALUE) {
    return fromMap as SingleValueFilter<CustomActivityTaxonomySelection>;
  }
  return effectiveFilters.find(
    (f): f is SingleValueFilter<CustomActivityTaxonomySelection> =>
      f.id === FilterId.ACTIVITY_TAXONOMY && f.type === FilterType.VALUE
  );
};

export const getV3Taxonomies = (
  effectiveFilters: Filter[],
  activeFilterMap?: ReadonlyMap<FilterId, Filter>
): {
  role_taxonomy: number;
  skill_taxonomy: number;
  activity_taxonomy: number;
} => {
  const roleTaxonomyFilter = readRoleTaxonomyFilter(
    effectiveFilters,
    activeFilterMap
  );

  const roleSelection = roleTaxonomyFilter?.value;
  const role_taxonomy = (() => {
    if (!roleSelection) return 0;
    if ('default' in roleSelection && roleSelection.default === true) return 0;
    return mapRoleTaxonomySelectionToId(
      stripVersionFromTaxonomyId(roleSelection)
    );
  })();

  const skillTaxonomyFilter = readSkillTaxonomyFilter(
    effectiveFilters,
    activeFilterMap
  );

  const skillVal = skillTaxonomyFilter?.value;
  const skill_taxonomy =
    skillVal && typeof skillVal.taxonomyId === 'number'
      ? skillVal.taxonomyId
      : DEFAULT_SKILL_TAXONOMY_ID;

  const activityTaxonomyFilter = readActivityTaxonomyFilter(
    effectiveFilters,
    activeFilterMap
  );

  const activityVal = activityTaxonomyFilter?.value;
  const activity_taxonomy =
    activityVal && typeof activityVal.taxonomyId === 'number'
      ? activityVal.taxonomyId
      : DEFAULT_ACTIVITY_TAXONOMY_ID;

  return { role_taxonomy, skill_taxonomy, activity_taxonomy };
};
